<template>
  <BaseModal :modalTitle="$t('W: Ansvarlig')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiComboboxVee
        name="userID"
        :label="$t('W: Ansvarlig')"
        :placeholder="$t('W: Ingen ansvarlig')"
        :options="userOptions"
        v-model="form.userID" />
      <template #buttons>
        <UiBaseButton
          :text="$t('W: Gem')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { mapState, mapActions } from 'pinia'
import { useAccountStore } from '@/stores/accountStore'
import { useUserStore } from '@/stores/userStore'
import { useOrderStore } from '@/stores/orderStore'
import { useModalStore } from '@/stores/modalStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'OrderReportResponsibleUserModal',

  emits: ['close'],

  props: {
    responsibleUserKey: { type: String, required: true },
    orderID: { type: String, required: true },
    currentUserID: { type: String, default: null },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        userID: this.currentUserID ?? '',
      },
      isLoading: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAccountStore, ['allLabAccountUsers']),
    ...mapState(useUserStore, ['currentUser']),

    myUserID(): string {
      return this.currentUser?.id ?? ''
    },

    // A user may only ever assign themselves or clear an existing assignment (see the
    // plan's Decisions Made) — never pick an arbitrary other user. The current assignee
    // is always included first so the combobox's initial value resolves to a real option,
    // even when that assignee is neither "me" nor cleared yet.
    // TODO: once roles/permissions exist, only offer "myself" when the current user is
    // allowed to be a responsible user (e.g. can edit analysis results) — for now everyone is.
    userOptions(): { value: string; text: string }[] {
      const options: { value: string; text: string }[] = []

      if (this.currentUserID) {
        const currentAssignee = this.allLabAccountUsers.find(
          (x) => x.id === this.currentUserID
        )
        options.push({
          value: this.currentUserID,
          text: currentAssignee?.fullName ?? this.currentUserID,
        })
        options.push({ value: '', text: this.$t('W: Ingen ansvarlig') })
      }

      if (this.myUserID && this.currentUserID !== this.myUserID) {
        options.push({
          value: this.myUserID,
          text: this.currentUser?.fullName ?? '',
        })
      }

      return options
    },
  },

  methods: {
    ...mapActions(useOrderStore, [
      'updateOrderReportResponsibleUserAssignment',
    ]),
    ...mapActions(useModalStore, ['open', 'closeTop']),

    async onSubmit(): Promise<void> {
      // No actual change (e.g. I'm already the assignee and re-submit as-is) — nothing to
      // confirm or save, just close.
      if (this.form.userID === (this.currentUserID ?? '')) {
        this.closeTop()
        return
      }
      if (this.form.userID === this.myUserID) {
        this.open({
          name: 'EnterPinModal',
          width: 'sm',
          props: {
            onConfirm: () => this.saveAssignment(this.myUserID),
          },
        })
        return
      }
      await this.saveAssignment(this.form.userID || null)
    },

    async saveAssignment(userID: string | null): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.updateOrderReportResponsibleUserAssignment(
          this.orderID,
          this.responsibleUserKey,
          userID
        )
        // Close EnterPinModal (currently on top, if opened) and this modal. Two closeTop()
        // calls rather than $emit('close') — by the time EnterPinModal's own post-onConfirm
        // $emit('close') runs, it's already unmounted and that emit is a no-op, so this is
        // the only place that actually needs to close both (see EnterAnalysisResultsModal.vue
        // for the same pattern).
        this.closeTop()
        this.closeTop()
        useToast().success(this.$t('W: Ansvarlig gemt'))
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('W: Kunne ikke gemme ansvarlig'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
